<template>
    <div class="w-full h-full">
        <form @submit.prevent="submitForm" action="" class="h-full w-full flex flex-col justify-between">
            <div class="p-2 pb-6 text-justify flex flex-col">
                <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                    <strong>Never paste credentials here.</strong> BIOS / BitLocker / recovery keys / passwords live exclusively in the password manager. Free-text fields are scanned and obvious credentials will be rejected.
                </div>

                <div class="w-full text-base border-b p-1 mb-2 tracking-widest flex justify-between items-center">
                    <span>Inventory Item Data</span>
                    <div class="flex items-center space-x-2">
                        <span class="text-xs px-2 py-0.5 rounded" :class="lifecycleBadgeClass">{{ lifecycleLabel }}</span>
                        <span v-if="ageLabel" class="text-xs text-gray-600">Age: {{ ageLabel }}</span>
                        <span v-if="refreshBand" class="text-xs px-2 py-0.5 rounded" :class="refreshBandClass">Refresh: {{ refreshBand }}</span>
                    </div>
                </div>
                <div class="grid grid-cols-4 gap-2 text-sm">
                    <!-- Row 1: identity + classification (mirrors first table columns) -->
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Device Name (e.g. SC-LT-YY-NN)</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.asset_name">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Serial Number</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.serial_number">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Category</label>
                        <select
                            class="border p-1 border-gray-400 mt-1 rounded-md w-full"
                            id="category"
                            v-model="formData.category"
                        >
                            <option v-for="category in inventoryCategoryOptions" :key="category.value" :value="category.value">{{ category.label }}</option>
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Brand</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.brand">
                    </div>

                    <!-- Row 2: rest of the at-a-glance fields the table shows -->
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Model*</label>
                        <input type="text" :required="!formData.brand && !formData.category" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.model">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Condition</label>
                        <input type="text" required class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.condition">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Cost</label>
                        <input type="number" step="0.01" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.cost">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Location</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.location">
                    </div>

                    <!-- Row 3: free-text description spans full width -->
                    <div class="flex flex-col col-span-4">
                        <label class="tracking-widest text-gray-500" for="">Description</label>
                        <textarea class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.description"></textarea>
                    </div>

                    <div class="flex flex-col col-span-4">
                        <label class="tracking-widest text-gray-500" for="">Notes</label>
                        <textarea class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.notes"></textarea>
                    </div>

                    <!-- Row 4: purchase / order info grouped together -->
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Purchase Date</label>
                        <input type="date" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.purchase_date">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Purchase Vendor</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.purchase_vendor">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Order Number</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.order_number">
                    </div>
                </div>

                <!-- Warranty subsection -->
                <div class="w-full text-base border-b p-1 my-2 tracking-widest flex justify-between items-center">
                    <span>Warranty</span>
                    <span v-if="warrantyStatusLabel" class="text-xs px-2 py-0.5 rounded" :class="warrantyBadgeClass">{{ warrantyStatusLabel }}</span>
                </div>
                <div class="grid grid-cols-4 gap-2 text-sm">
                    <div class="flex flex-col col-span-2">
                        <label class="tracking-widest text-gray-500">Warranty (shared contract)</label>
                        <div class="flex space-x-1">
                            <select v-model="formData.warranty" class="p-1 border mt-1 border-gray-400 rounded-md flex-1">
                                <option :value="null">(none)</option>
                                <option v-for="w in warrantyOptions" :key="w.id" :value="w.id">{{ w.name }}<span v-if="w.default_end"> · ends {{ w.default_end }}</span></option>
                            </select>
                            <button type="button" @click="showCreateWarranty = true" class="border rounded px-2 mt-1 text-sm hover:bg-gray-50">+ New</button>
                        </div>
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Warranty Number (per-unit ID)</label>
                        <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.warranty_number">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Warranty Start (override)</label>
                        <input type="date" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.warranty_start_override">
                    </div>
                    <div class="flex flex-col">
                        <label class="tracking-widest text-gray-500" for="">Warranty End (override)</label>
                        <input type="date" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.warranty_end_override">
                    </div>
                </div>

                <!-- Laptop spec subsection -->
                <div v-if="isLaptop" class="mt-4">
                    <div class="w-full text-base border-b p-1 my-2 tracking-widest">Laptop Specs</div>
                    <div class="grid grid-cols-4 gap-2 text-sm">
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">Processor</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.processor">
                        </div>
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">Memory</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.memory" placeholder="40 GB">
                        </div>
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">Storage</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.storage" placeholder="932 GB">
                        </div>
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">Screen Size</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.screen_size" placeholder="16 in">
                        </div>
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">GPU</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.gpu">
                        </div>
                        <div class="flex flex-col">
                            <label class="tracking-widest text-gray-500">MAC Address (non-secret)</label>
                            <input type="text" class="p-1 border mt-1 border-gray-400 rounded-md" v-model="formData.mac_address">
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex w-ful justify-center">
                <base-button-affirmative class="mx-1">Save</base-button-affirmative>
                <base-button-affirmative @click.prevent="saveAndClose" class="mx-1">Save and Close</base-button-affirmative>
                <base-button-negative class="mx-1" @click.prevent="cancel">Cancel</base-button-negative>
            </div>

            <base-modal-small v-if="showCreateWarranty" @close="showCreateWarranty = false">
                <template #header>New Warranty</template>
                <template #body>
                    <div class="space-y-2 p-2 text-sm">
                        <div class="flex flex-col">
                            <label class="text-gray-500">Name*</label>
                            <input type="text" v-model="newWarranty.name" required class="border rounded p-1" placeholder="Dell ProSupport — 2026 fleet" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Provider</label>
                            <input type="text" v-model="newWarranty.provider" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Contract Number</label>
                            <input type="text" v-model="newWarranty.contract_number" class="border rounded p-1" />
                        </div>
                        <div class="flex flex-col">
                            <label class="text-gray-500">Coverage terms</label>
                            <textarea v-model="newWarranty.coverage_terms" class="border rounded p-1"></textarea>
                        </div>
                        <div class="flex space-x-2">
                            <div class="flex flex-col flex-1">
                                <label class="text-gray-500">Start</label>
                                <input type="date" v-model="newWarranty.default_start" class="border rounded p-1" />
                            </div>
                            <div class="flex flex-col flex-1">
                                <label class="text-gray-500">End</label>
                                <input type="date" v-model="newWarranty.default_end" class="border rounded p-1" />
                            </div>
                        </div>
                        <div class="flex justify-end space-x-2 pt-2">
                            <button type="button" @click="showCreateWarranty = false" class="px-3 py-1 border rounded">Cancel</button>
                            <button type="button" :disabled="!newWarranty.name || busy" @click="submitNewWarranty" class="px-3 py-1 rounded bg-dark-primary text-white">Create</button>
                        </div>
                    </div>
                </template>
            </base-modal-small>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, reactive, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { listWarranties, createWarranty } from '@/services/Inventory';

const props = defineProps([
    'inventoryItem',
    'userOptions',
    'inventoryCategoryOptions',
])

const store = useStore()
const $toast = useToast()
const busy = ref(false);

const emit = defineEmits(['submit-form', 'cancel', 'close'])

const formData = reactive({
    item_name: props.inventoryItem.item_name,
    condition: props.inventoryItem.condition,
    cost: props.inventoryItem.cost,
    description: props.inventoryItem.description,
    serial_number: props.inventoryItem.serial_number,
    file_attachments: props.inventoryItem.file_attachments,
    model: props.inventoryItem.model,
    brand: props.inventoryItem.brand,
    location: props.inventoryItem.location,
    purchase_vendor: props.inventoryItem.purchase_vendor,
    warranty_number: props.inventoryItem.warranty_number,
    order_number: props.inventoryItem.order_number,
    category: props.inventoryItem.category ? props.inventoryItem.category.id : null,
    // new fields
    asset_name: props.inventoryItem.asset_name,
    purchase_date: props.inventoryItem.purchase_date,
    processor: props.inventoryItem.processor,
    memory: props.inventoryItem.memory,
    storage: props.inventoryItem.storage,
    screen_size: props.inventoryItem.screen_size,
    gpu: props.inventoryItem.gpu,
    mac_address: props.inventoryItem.mac_address,
    warranty: props.inventoryItem.warranty ? props.inventoryItem.warranty.id : null,
    warranty_start_override: props.inventoryItem.warranty_start_override,
    warranty_end_override: props.inventoryItem.warranty_end_override,
    notes: props.inventoryItem.notes,
})

const warrantyOptions = ref([]);
const showCreateWarranty = ref(false);
const newWarranty = reactive({
    name: '', provider: '', contract_number: '', coverage_terms: '',
    default_start: null, default_end: null,
});

async function loadWarranties() {
    try {
        const resp = await listWarranties(store.state.auth.token, '?page_size=200');
        warrantyOptions.value = resp.data?.results || resp.data || [];
    } catch (e) {
        $toast.error(`Could not load warranties: ${e?.message || e}`);
    }
}

onMounted(loadWarranties);

async function submitNewWarranty() {
    busy.value = true;
    try {
        const payload = { ...newWarranty };
        Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
        const resp = await createWarranty(payload, store.state.auth.token);
        warrantyOptions.value = [...warrantyOptions.value, resp.data];
        formData.warranty = resp.data.id;
        showCreateWarranty.value = false;
        $toast.success('Warranty created.');
    } catch (e) {
        $toast.error(e?.response?.data ? JSON.stringify(e.response.data) : (e?.message || String(e)));
    } finally {
        busy.value = false;
    }
}

const categoryName = computed(() => {
    if (!formData.category) return '';
    const opt = props.inventoryCategoryOptions?.find(o => o.value === formData.category);
    return (opt?.label || '').toLowerCase();
});
const isLaptop = computed(() => categoryName.value === 'laptop');

const lifecycleLabel = computed(() => ({
    in_stock: 'Available', assigned: 'Assigned',
    returned: 'Needs review', retired: 'Retired',
}[props.inventoryItem.lifecycle_state] || props.inventoryItem.lifecycle_state));
const lifecycleBadgeClass = computed(() => ({
    in_stock: 'bg-green-100 text-green-800',
    assigned: 'bg-blue-100 text-blue-800',
    returned: 'bg-amber-100 text-amber-800',
    retired: 'bg-gray-300 text-gray-700',
}[props.inventoryItem.lifecycle_state] || 'bg-gray-100 text-gray-700'));

const ageLabel = computed(() => {
    const age = props.inventoryItem.age;
    if (!age) return null;
    if (age.years === 0) return `${age.months} mo`;
    return `${age.years}y ${age.months}m`;
});
const refreshBand = computed(() => props.inventoryItem.refresh_band);
const refreshBandClass = computed(() => ({
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
}[refreshBand.value] || ''));

const warrantyStatusLabel = computed(() => ({
    active: 'active', expiring_soon: 'expiring soon',
    expired: 'expired', unknown: 'unknown',
}[props.inventoryItem.warranty_status] || ''));
const warrantyBadgeClass = computed(() => ({
    active: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-amber-100 text-amber-800',
    expired: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-200 text-gray-700',
}[props.inventoryItem.warranty_status] || ''));

function saveAndClose(){
    submitForm(true)
}

function cancel(){
    emit('cancel')
}

function submitForm(closeForm=false){
    // Normalize empty strings → null on optional date / FK fields so the server
    // accepts them as "cleared".
    const cleaned = { ...formData };
    ['purchase_date', 'warranty', 'warranty_start_override', 'warranty_end_override']
        .forEach(k => { if (cleaned[k] === '') cleaned[k] = null; });

    const data = {
        inventory_item_data: cleaned,
    }
    emit('submit-form', data, closeForm)
}
</script>
